// Vendor
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';

// Components
import { LoadingDots, Trans } from '../../../components';
import AgencyCard from '../../../components/common/Cards/AgencyCard/AgencyCard';

import Interstitial from '../../../components/Interstitial/Interstitial';

// Actions
import { getMyApps } from '../../../reducers/agency/agencyActions';
import {
  selectAgencyToLink,
  clearLinkServiceData,
} from '../../../reducers/agency/linkService/linkServiceActions';
import { selectAllAgenciesAsArray } from '../../../reducers/agency/agencySelectors';
import { clearTakeoverNavContent } from '../../../reducers/navbar/navbarActions';
import { sortAgencies } from '../../../lib/helpers/util';
import { openModal } from '../../../reducers/modal/modalActions';

// Modals
import { UnlockMoreTxTServicesModal } from '../../../components/common/modals';

//helpers
import { getAgencyDataBySlug } from '../../../lib/helpers';

// Constants
import { AGENCY_SERVICE_KEYS } from './linkServiceSelectAgencyConstants';

const LinkServiceSelectAgency = ({
  agencies,
  interstitialAgencyCode,
  selectAgencyToLink,
  clearLinkServiceData,
  clearTakeoverNavContent,
  openModal,
  getMyApps,
  isGovernmentEmployee,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useTranslation();

  /**
   * PRJ1417943 - Integration Accelerator
   * Replaced multiple useTranslation hooks and nested ternaries with dynamic namespace
   * To simplify agency name translation and reduce code duplication
   */
  const getTranslatedAgencyName = (agencySlug, fallbackName) => {
    const translatedName = t('AgencyName', { ns: agencySlug });
    return translatedName === 'AgencyName' ? fallbackName : translatedName;
  };

  useEffect(() => {
    if (!agencies.length) {
      getMyApps().then(() => setIsLoading(false));
    } else {
      setIsLoading(false);

      clearLinkServiceData();
      clearTakeoverNavContent();
    }
  }, []);

  const handleAgencySelect = async agency => {
    setIsLoading(true);

    await selectAgencyToLink(agency);
    setIsLoading(false);
  };

  const handleAgencyCardClick = agency => {
    if (agency.agencyAllowed === 'N') {
      openModal('UnlockMoreTxTServicesModal');
      return;
    }

    handleAgencySelect(agency);
  };

  const getAgencyServiceText = agencySlug => {
    const keys = AGENCY_SERVICE_KEYS[agencySlug];
    if (!keys) return [];
    return keys.map(key => t(key, { ns: agencySlug }));
  };

  const getAgencyServices = (agencySlug, apps) => (
    <ul>
      {getAgencyServiceText(agencySlug).length > 0
        ? getAgencyServiceText(agencySlug).map(service => (
            <li key={service}>{service}</li>
          ))
        : apps.map(app => (
            <li key={app.appId}>
              <Trans
                file={agencySlug}
                id={`${app.appSlug}_AppName`}
                fallback={app.appName}
              />
            </li>
          ))}
    </ul>
  );

  if (isLoading) return <LoadingDots />;

  return (
    <>
      {!interstitialAgencyCode && (
        <>
          <h1 className="h2 link-service-select-agency-title">
            <Trans
              file="LinkService"
              id="SelectAgencyTitle"
              fallback="Select an agency"
            />
          </h1>
          <p className="link-service-select-agency-subtitle">
            <Trans
              file="LinkService"
              id="SelectAgencyDesc"
              fallback="Please choose the agency that sends your license or registration."
            />
          </p>
          <ul
            className="link-service-select-agency-list"
            aria-label="list of agencies"
          >
            {agencies
              .filter(agency => {
                // DPS Agency need to hide if user already linked DL/ID/CDL to DPS Account
                if (
                  agency.linkUserKeys &&
                  agency.linkUserKeys.length > 0 &&
                  agency.agencyId === '10'
                ) {
                  return false;
                }
                if (!isGovernmentEmployee) {
                  return agency.agencyId !== '7';
                }
                return true;
              })
              .sort(sortAgencies)
              .map(agency => {
                const { agencyId, agencySlug, apps } = agency;
                const { name, logo } = getAgencyDataBySlug(agencySlug);
                return (
                  <li key={agencyId}>
                    <a className="text-decoration-none">
                      <AgencyCard
                        outlined={true}
                        onClick={() => handleAgencyCardClick(agency)}
                        agencySlug={agencySlug}
                        variant="link-service-select"
                        className="link-service-select-agency-card"
                        showChevron={true}
                        isLocked={agency.agencyAllowed === 'N'}
                        agency={{
                          name: getTranslatedAgencyName(agencySlug, name),
                          logo,
                        }}
                        numLicenses={apps.length.toString()}
                        bodyHeading={
                          <Trans
                            file="Labels"
                            id="ServicesAvailable"
                            fallback="Available services"
                          />
                        }
                        moreInfoText={getAgencyServices(agencySlug, apps)}
                        lockedRequirement={
                          agency.agencyAllowed === 'N' ? (
                            <Trans
                              file="LinkService"
                              id="LockedAgencyRequirement"
                              fallback="Requires Texas driver license or ID"
                            />
                          ) : null
                        }
                      />
                    </a>
                  </li>
                );
              })}
            <li>
              <p className="link-service-more-agencies-copy">
                <Trans
                  file="Common"
                  id="MoreAgenciesComingSoon"
                  fallback="More agencies coming soon!"
                />
              </p>
            </li>
          </ul>
        </>
      )}

      <UnlockMoreTxTServicesModal />

      {interstitialAgencyCode && (
        <Interstitial interstitialAgencyCode={interstitialAgencyCode} />
      )}
    </>
  );
  //   }
};

const mapStateToProps = state => ({
  agencies: selectAllAgenciesAsArray(state),
  isGovernmentEmployee: state.user.profile.isGovernmentEmployee,
  interstitialAgencyCode: state.router.location.appSlug,
});
const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      getMyApps,
      selectAgencyToLink,
      clearLinkServiceData,
      clearTakeoverNavContent,
      openModal,
    },
    dispatch
  );

LinkServiceSelectAgency.propTypes = {
  getMyApps: PropTypes.func,
  agencies: PropTypes.arrayOf(PropTypes.object),
  clearLinkServiceData: PropTypes.func,
  clearTakeoverNavContent: PropTypes.func,
  openModal: PropTypes.func,
  selectAgencyToLink: PropTypes.func,
  isGovernmentEmployee: PropTypes.bool,
  interstitialAgencyCode: PropTypes.string,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(LinkServiceSelectAgency);